import type { TFunction } from 'i18next';
import type { DamageType } from "./damage";

export const WorkPackageStatus = {
	Pending: 'Pending',
	Assigned: 'Assigned',
	Completed: 'Completed',
} as const;

export type WorkPackageStatus = (typeof WorkPackageStatus)[keyof typeof WorkPackageStatus];

/** Translates a stored `WorkPackageStatus` value for display; the stored value itself never changes. */
export function workPackageStatusLabel(t: TFunction, status: WorkPackageStatus | undefined): string {
	switch (status) {
		case WorkPackageStatus.Pending:
			return t('finance.status.pending');
		case WorkPackageStatus.Assigned:
			return t('finance.status.assigned');
		case WorkPackageStatus.Completed:
			return t('finance.status.completed');
		default:
			return t('common.states.unknown');
	}
}

/**
 * Translates a stored line-item unit code for display. `cm`/`m²`/`m³` are real SI symbols and stay
 * as-is in every language; `pcs` is an English word and needs a German label. The stored value
 * itself never changes.
 */
export function unitTypeLabel(t: TFunction, unitType: string | null | undefined): string {
	if (unitType === 'pcs') return t('finance.items.unitPieces');
	return unitType ?? '';
}

export type WorkPackage = Partial<{
	id: string;
	assetId: string;
	title: string | null;
	description: string | null;
	vat: number | null;
	netAmount: string | number | null;
	grossAmount: string | number | null;
	taxAmount: string | number | null;
	status: WorkPackageStatus;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	createRequestId: string | null;
	updateRequestId: string | null;
	createdById: string | null;
	updatedById: string | null;

	workPackageItems?: WorkPackageItem[];
}>;

export type WorkPackageItem = Partial<{
	id: string;
	workPackageId: string;
	damageId: string | null;
	description: string | null;
	quantity: number | null;
	unitType: string | null;
	unitPrice: string | number | null;
	totalPrice: string | number | null;
	parentItemId: string | null;
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
	createRequestId: string | null;
	updateRequestId: string | null;
	createdById: string | null;
	updatedById: string | null;
  damage: DamageType | null;
}>;
